import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Button } from '@mui/material';
import { GRID_DETAIL_PANEL_TOGGLE_FIELD } from '@mui/x-data-grid-premium';
import IRDataGrid from 'features/IRWorkstation/components/Common/IRDataGrid';
import { AlertSubscriptionColumnDefinitions } from 'features/IRWorkstation/components/Common/DataGrid/ColumnDefinitions/AlertSubscriptionColumnDefinitions.jsx';
import { AlertRuleMetric } from 'features/IRWorkstation/components/AlertCenter/AlertSubscriptionList/AlertSubscriptionColumnDisplay';
import RunAlertModal from 'features/IRWorkstation/components/AlertCenter/RunAlertModal';
import { useIRWorkstation } from 'features/IRWorkstation/contexts/IRWorkstationContext';

// Track Events
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';

// -------------------------------------------------------------------------
// Filters
// -------------------------------------------------------------------------

const FilterButtons = ({ filter, setFilter, onFilterClick, disabled }) => (
	<Box
		sx={{
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			mb: 4,
			flexShrink: 0,
		}}
	>
		<Box sx={{ display: 'flex', gap: 1 }}>
			{['Subscribed', 'All'].map((filterOption) => (
				<Button
					key={filterOption}
					variant={
						filter === filterOption.toLowerCase() ? 'contained' : 'outlined'
					}
					size='small'
					onClick={() => {
						onFilterClick(filterOption.toLowerCase());
						setFilter(filterOption.toLowerCase());
					}}
					disabled={disabled}
				>
					{filterOption}
				</Button>
			))}
		</Box>
	</Box>
);

FilterButtons.propTypes = {
	filter: PropTypes.string.isRequired,
	setFilter: PropTypes.func.isRequired,
	onFilterClick: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
};

// -------------------------------------------------------------------------
// Alert Subscription Table
// -------------------------------------------------------------------------

const alertTableSx = {
	width: '100%',
	minWidth: 0,
	flex: 1,
	minHeight: 0,
	backgroundColor: 'background.paper',
	border: '0.5px solid',
	borderColor: 'border.light',
	borderRadius: '12px',
	overflow: 'hidden',
	'&& .MuiDataGrid-columnHeaders, && .MuiDataGrid-columnHeader, && .MuiDataGrid-columnHeaderTitleContainer, && .MuiDataGrid-filler, && .MuiDataGrid-scrollbarFiller':
		{
			backgroundColor: 'background.paper',
		},
	'&& .MuiDataGrid-columnHeaderTitle': {
		color: 'text.tertiary',
		textTransform: 'uppercase',
	},
	'&& .MuiDataGrid-detailPanel': {
		backgroundColor: 'background.paper',
	},
	'&& .MuiDataGrid-row:hover + .MuiDataGrid-detailPanel': {
		backgroundColor: 'background.subtle',
	},
	'&& .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader, && .MuiDataGrid-row--borderBottom .MuiDataGrid-filler, && .MuiDataGrid-row--borderBottom .MuiDataGrid-scrollbarFiller':
		{
			borderBottomWidth: '0.5px',
			borderBottomColor: 'border.light',
		},
	'&& .MuiDataGrid-columnHeader, && .MuiDataGrid-cell': {
		paddingLeft: '15px',
	},
	'&& .MuiDataGrid-columnHeader--last .MuiDataGrid-columnSeparator': {
		display: 'none',
	},
	'&& .MuiDataGrid-columnHeader:focus, && .MuiDataGrid-columnHeader:focus-within, && .MuiDataGrid-cell:focus, && .MuiDataGrid-cell:focus-within':
		{
			outline: 'none',
		},
};

const AlertSubscriptionTable = ({
	alertSubscriptions,
	loading,
	noRowsMessage,
	handleSubscribe,
	handleUnsubscribe,
	handleDelete
}) => {	
	const [expandedRowIds, setExpandedRowIds] = useState([]);
	const [runNowRule, setRunNowRule] = useState(null);
	const { tenant } = useIRWorkstation();
	const { trackButtonClick } = useTrackEvent({ tenant, product: 'IEM' });

	// if expanded (id exist in array) => remove id from array so we can hide expanded content
	// if not expanded (id DNE in array) => add id to array so we can expand it
	const handleToggleRule = (rowId, isRuleExpanded) => {
		trackButtonClick(
			isRuleExpanded
				? ButtonClickTargets.AlertCenterCollapseRule
				: ButtonClickTargets.AlertCenterExpandRule
		);
		setExpandedRowIds((ids) =>
			ids.includes(rowId) ? ids.filter((id) => id !== rowId) : [...ids, rowId]
		);
	};

	const handleViewReportInCrm = () => {
		trackButtonClick(ButtonClickTargets.AlertCenterViewReportInCrm);
	};

	const handleDeletePressed = (selectedAlertRule) => {
		if (!window.confirm('Are you sure you want to delete this alert? All alert subscriptions will be deleted as well.')) {
			return;
		}
		handleDelete(selectedAlertRule);
	}


	const columns = AlertSubscriptionColumnDefinitions({
		handleSubscribe,
		handleUnsubscribe,
		handleRunNow: (row) => setRunNowRule(row),
		handleDeleteRule: (row) => handleDeletePressed(row),
		expandedRowIds,
		onToggleRule: handleToggleRule,
		onViewReportInCrm: handleViewReportInCrm
	});

	return (
		<Box sx={alertTableSx}>
			{runNowRule && (
				<RunAlertModal
					open
					onClose={() => setRunNowRule(null)}
					rule={runNowRule}
				/>
			)}

			<IRDataGrid
				rows={alertSubscriptions}
				columns={columns}
				loading={loading}
				noRowsMessage={noRowsMessage}
				getRowHeight={() => 'auto'}
				columnHeaderHeight={48}
				maxHeight='100%'
				fillHeight
				getDetailPanelContent={({ row }) => (
					<AlertRuleMetric metric={row.metric} schedule={row.schedule} />
				)}
				getDetailPanelHeight={() => 'auto'}
				detailPanelExpandedRowIds={expandedRowIds}
				onDetailPanelExpandedRowIdsChange={setExpandedRowIds}
				columnVisibilityModel={{ [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false }}
			/>
		</Box>
	);
};

AlertSubscriptionTable.propTypes = {
	alertSubscriptions: PropTypes.arrayOf(PropTypes.object).isRequired,
	loading: PropTypes.bool,
	noRowsMessage: PropTypes.string,
	handleSubscribe: PropTypes.func,
	handleUnsubscribe: PropTypes.func,
	handleDelete: PropTypes.func,
};

const AlertSubscriptionList = ({
	subscribedAlerts,
	allAlerts,
	loading,
	subscribe,
	unsubscribe,
	handleDelete,
	filter,
	setFilter,
	error,
	onFilterClick,
}) => {
	const visibleAlerts = filter === 'subscribed' ? subscribedAlerts : allAlerts;
	const emptyMessage =
		error || (filter === 'subscribed' ? 'No subscribed alerts' : 'No alerts');

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
			}}
		>
			<FilterButtons
				filter={filter}
				setFilter={setFilter}
				onFilterClick={onFilterClick}
				disabled={loading || !!error}
			/>

			<AlertSubscriptionTable
				alertSubscriptions={visibleAlerts}
				loading={loading}
				noRowsMessage={emptyMessage}
				handleSubscribe={subscribe}
				handleUnsubscribe={unsubscribe}
				handleDelete={handleDelete}
			/>
		</Box>
	);
};

AlertSubscriptionList.propTypes = {
	allAlerts: PropTypes.array.isRequired,
	subscribedAlerts: PropTypes.array.isRequired,
	loading: PropTypes.bool.isRequired,
	subscribe: PropTypes.func.isRequired,
	unsubscribe: PropTypes.func.isRequired,
	handleDelete: PropTypes.func.isRequired,
	filter: PropTypes.string.isRequired,
	setFilter: PropTypes.func.isRequired,
	error: PropTypes.string,
	onFilterClick: PropTypes.func.isRequired,
};

export default AlertSubscriptionList;
